import type { V1CodeMapping } from '@/generated/hooks/grimoire/models/v1CodeMapping';
import type { Commonv1Location } from '@/generated/hooks/astral/models/commonv1Location';

/**
 * Represents a contiguous range of characters in a string.
 */
export interface ICharacterRange {
  /**
   * The start character offset, zero-based, inclusive
   */
  start: number;

  /**
   * The end character offset, zero-based, exclusive
   */
  end: number;
}

/**
 * Maps a spy code byte location to corresponding AOP (english) code byte locations using the code mapping.
 *
 * @param spyLocation - The location in spy code to map
 * @param codeMapping - The code mapping containing spy-to-english mappings
 * @returns Array of english code byte locations that overlap with the spy byte location
 *
 * @example
 * ```ts
 * const aopLocations = mapSpyToAopLocations(
 *   { start_byte: "50", end_byte: "100" },
 *   codeMapping
 * );
 * // Returns array of english code byte locations that correspond to the spy byte location
 * ```
 */
export function mapSpyToAopLocations({
  spyLocation,
  codeMapping,
}: {
  spyLocation: Commonv1Location;
  codeMapping: V1CodeMapping;
}): Commonv1Location[] {
  const spyStart = Number(spyLocation.start_byte);
  const spyEnd = Number(spyLocation.end_byte);

  if (isNaN(spyStart) || isNaN(spyEnd)) {
    console.warn('Invalid byte offsets in spy location', spyLocation);
    return [];
  }

  const aopLocations: Commonv1Location[] = [];

  // Find all mapping entries where the spy_location overlaps with our input location
  for (const entry of codeMapping.entries || []) {
    if (!entry.spy_location?.start_byte || !entry.spy_location?.end_byte) {
      console.warn('Invalid spy byte offsets in code mapping entry', entry);
      continue;
    }

    const entrySpyStart = Number(entry.spy_location.start_byte);
    const entrySpyEnd = Number(entry.spy_location.end_byte);

    if (isNaN(entrySpyStart) || isNaN(entrySpyEnd)) {
      console.warn('Invalid spy byte offsets in code mapping entry', entry);
      continue;
    }

    // Check if ranges overlap
    // For zero-length ranges (spyStart === spyEnd), check if the position is within the entry
    // For non-zero ranges, use standard overlap check: range1.start < range2.end && range2.start < range1.end
    const isZeroLength = spyStart === spyEnd;
    const overlaps = isZeroLength
      ? spyStart >= entrySpyStart && spyStart < entrySpyEnd
      : spyStart < entrySpyEnd && entrySpyStart < spyEnd;

    if (
      overlaps &&
      entry.english_location?.start_byte &&
      entry.english_location?.end_byte
    ) {
      aopLocations.push(entry.english_location);
    }
  }

  return aopLocations;
}

/**
 * Converts a byte offset to a character offset in UTF-8 encoded text.
 *
 * This function handles multi-byte characters correctly by decoding the byte
 * array up to the specified offset and counting the resulting characters.
 *
 * @param aopBytes - The UTF-8 encoded byte array of the text
 * @param offset - The byte offset as a number
 * @returns The character position (zero-based) corresponding to the byte offset
 *
 * @example
 * ```ts
 * const text = "Hello 世界";
 * const bytes = new TextEncoder().encode(text);
 * const charOffset = getCharacterOffset(bytes, 8);
 * // Returns 6 (the position of "世" in characters, not bytes)
 * ```
 */
function getCharacterOffset(aopBytes: Uint8Array, offset: number): number {
  const decoder = new TextDecoder();
  return decoder.decode(aopBytes.slice(0, offset)).length;
}

/**
 * Maps a spy code byte location to corresponding AOP (english) character ranges.
 *
 * This function combines location mapping with byte-to-character conversion to
 * provide character-based ranges suitable for text editor highlighting or
 * selection. It handles multi-byte UTF-8 characters.
 *
 * @param params - The mapping parameters
 * @param params.spyLocation - The location in spy code to map
 * @param params.codeMapping - The code mapping containing spy-to-english mappings
 * @param params.aopText - The full AOP/english text to calculate character offsets
 * @returns Array of character ranges (zero-based, start inclusive, end exclusive)
 *
 * @example
 * ```ts
 * const ranges = mapSpyLocationToAopCharacterRanges({
 *   spyLocation: { start_byte: "50", end_byte: "100" },
 *   codeMapping,
 *   aopText: "when the user says hello..."
 * });
 * // Returns: [{ start: 10, end: 25 }]
 * // These character offsets can be used for text highlighting
 * ```
 */
export function mapSpyLocationToAopCharacterRanges({
  spyLocation,
  codeMapping,
  aopText,
}: {
  spyLocation: Commonv1Location;
  codeMapping: V1CodeMapping;
  aopText: string;
}): ICharacterRange[] {
  const encoder = new TextEncoder();
  const aopBytes = encoder.encode(aopText);
  const characterRanges: ICharacterRange[] = [];

  const aopLocations = mapSpyToAopLocations({ spyLocation, codeMapping });
  for (const location of aopLocations) {
    if (!location.start_byte || !location.end_byte) {
      console.warn('Invalid byte offsets in english location', location);
      continue;
    }
    const startByte = Number(location.start_byte);
    const endByte = Number(location.end_byte);
    if (isNaN(startByte) || isNaN(endByte)) {
      console.warn('Invalid byte offsets in english location', location);
      continue;
    }
    characterRanges.push({
      start: getCharacterOffset(aopBytes, startByte),
      end: getCharacterOffset(aopBytes, endByte),
    });
  }
  return characterRanges;
}

/**
 * Maps a spy byte position to the corresponding AOP (english) character position.
 *
 * This function finds the maximum AOP character position that corresponds to
 * any spy position up to and including the given spy byte position. This is useful
 * for determining "executed" ranges where we want to highlight from the start
 * of the document up to the current execution point.
 *
 * @param params - The mapping parameters
 * @param params.spyBytePosition - The spy byte position to map
 * @param params.codeMapping - The code mapping containing spy-to-english mappings
 * @param params.aopText - The full AOP/english text to calculate character offsets
 * @returns The AOP character position, or 0 if no mapping found
 *
 * @example
 * ```ts
 * const aopPosition = mapSpyBytePositionToAopCharacterPosition({
 *   spyBytePosition: 500,
 *   codeMapping,
 *   aopText: "when the user says hello..."
 * });
 * // Returns: 125 (character position in AOP text)
 * ```
 */
export function mapSpyBytePositionToAopCharacterPosition({
  spyBytePosition,
  codeMapping,
  aopText,
}: {
  spyBytePosition: number;
  codeMapping: V1CodeMapping;
  aopText: string;
}): number {
  if (isNaN(spyBytePosition) || spyBytePosition <= 0) {
    return 0;
  }

  const encoder = new TextEncoder();
  const aopBytes = encoder.encode(aopText);

  let maxAopByte = 0;

  // Find the maximum AOP byte position for any spy location
  // that ends at or before our target spy position
  for (const entry of codeMapping.entries || []) {
    if (!entry.spy_location?.end_byte || !entry.english_location?.end_byte) {
      continue;
    }

    const entrySpyEnd = Number(entry.spy_location.end_byte);
    const entryAopEnd = Number(entry.english_location.end_byte);

    if (isNaN(entrySpyEnd) || isNaN(entryAopEnd)) {
      continue;
    }

    // Include entry if its spy range ends at or before our position.
    // The +1 handles the case where spyBytePosition is the index of the last
    // executed byte (inclusive) while entrySpyEnd is exclusive (position after).
    // This ensures entries whose last byte was executed are included.
    if (entrySpyEnd <= spyBytePosition + 1) {
      maxAopByte = Math.max(maxAopByte, entryAopEnd);
    }
  }

  // Convert byte position to character position
  return getCharacterOffset(aopBytes, Math.min(maxAopByte, aopBytes.length));
}

/**
 * Maps a spy byte position to the maximum corresponding English byte position.
 *
 * This function finds the maximum English byte position that corresponds to
 * any spy position up to and including the given spy byte position. Unlike
 * `mapSpyBytePositionToAopCharacterPosition`, this returns byte positions
 * rather than character positions.
 *
 * @param params - The mapping parameters
 * @param params.spyBytePosition - The spy byte position to map
 * @param params.codeMapping - The code mapping containing spy-to-english mappings
 * @returns The maximum English byte position, or 0 if no mapping found
 *
 * @example
 * ```ts
 * const englishBytePosition = mapSpyBytePositionToEnglishBytePosition({
 *   spyBytePosition: 500,
 *   codeMapping,
 * });
 * // Returns: 300 (byte position in English text)
 * ```
 */
export function mapSpyBytePositionToEnglishBytePosition({
  spyBytePosition,
  codeMapping,
}: {
  spyBytePosition: number;
  codeMapping: V1CodeMapping;
}): number {
  if (isNaN(spyBytePosition) || spyBytePosition <= 0) {
    return 0;
  }

  let maxEnglishByte = 0;

  // Find the maximum English byte position for any spy location
  // that ends at or before our target spy position
  for (const entry of codeMapping.entries || []) {
    if (!entry.spy_location?.end_byte || !entry.english_location?.end_byte) {
      continue;
    }

    const entrySpyEnd = Number(entry.spy_location.end_byte);
    const entryEnglishEnd = Number(entry.english_location.end_byte);

    if (isNaN(entrySpyEnd) || isNaN(entryEnglishEnd)) {
      continue;
    }

    // If the spy entry ends at or before our position, include its English end
    if (entrySpyEnd <= spyBytePosition) {
      maxEnglishByte = Math.max(maxEnglishByte, entryEnglishEnd);
    }
  }

  return maxEnglishByte;
}

/**
 * Converts English byte positions directly to character positions.
 *
 * Unlike the spy-to-AOP mapping functions, this function converts English
 * byte positions directly to character positions without using code mapping.
 * This is useful for highlighting section headers that don't have
 * corresponding spy code.
 *
 * @param params - The conversion parameters
 * @param params.startByte - The start byte position in English code
 * @param params.endByte - The end byte position in English code
 * @param params.aopText - The AOP/English text to calculate character offsets
 * @returns A character range, or null if conversion fails
 *
 * @example
 * ```ts
 * const range = convertEnglishBytesToCharacterRange({
 *   startByte: 0,
 *   endByte: 50,
 *   aopText: "1. Calculate sum of first m prime numbers..."
 * });
 * // Returns: { start: 0, end: 50 } (for ASCII text, bytes = chars)
 * ```
 */
export function convertEnglishBytesToCharacterRange({
  startByte,
  endByte,
  aopText,
}: {
  startByte: number;
  endByte: number;
  aopText: string;
}): ICharacterRange | null {
  if (!aopText || isNaN(startByte) || isNaN(endByte)) {
    return null;
  }

  const encoder = new TextEncoder();
  const aopBytes = encoder.encode(aopText);

  // Clamp byte positions to valid range
  const clampedStart = Math.max(0, Math.min(startByte, aopBytes.length));
  const clampedEnd = Math.max(0, Math.min(endByte, aopBytes.length));

  // Convert byte positions to character positions
  const startChar = getCharacterOffset(aopBytes, clampedStart);
  const endChar = getCharacterOffset(aopBytes, clampedEnd);

  return { start: startChar, end: endChar };
}
